import React, {useMemo} from "react"
import {
	DataTable,
	DataTableActionHeader,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
} from "../DataTable/DataTable"
import {DataTableActionButton, DataTableActionItem} from "../DataTable/DataTableActionButton"
import {hiddenValue, isMasked} from "../../resources/common"
import {
	datePreview,
	emailPreview,
	identificationNumberPreview,
	InformationHiding,
	phonePreview,
} from "../InformationHiding/InformationHiding"
import {
	useIsBankOperationsUser,
	useIsBankReadOnlyUser,
	useIsUnitAdminUser,
	useIsUnitComplianceUser,
} from "../../services/auth"
import moment from "moment"
import {CustomerBeneficialOwner} from "../../resources/customerBeneficialOwner"
import {useModal} from "react-modal-hook"
import EditCustomerBeneficialOwnerModal from "./EditCustomerBeneficialOwnerModal"

interface CustomerBeneficialOwnerRowProps {
	customerId: string
	beneficialOwner: CustomerBeneficialOwner
	refresh: () => void
}

function getBeneficialOwnerDocument(bo: CustomerBeneficialOwner): string | undefined {
	if (bo.attributes.ssn || bo.attributes.maskedSSN) {
		return `${bo.attributes.nationality ? bo.attributes.nationality + " " : ""}SSN ${
			bo.attributes.ssn ?? bo.attributes.maskedSSN
		}`
	}

	if (bo.attributes.passport || bo.attributes.maskedPassport) {
		return `${bo.attributes.nationality ?? ""} Passport ${bo.attributes.passport ?? bo.attributes.maskedPassport}`
	}

	if (bo.attributes.matriculaConsular || bo.attributes.maskedMatriculaConsular) {
		return `Matricula Consular ${bo.attributes.matriculaConsular ?? bo.attributes.maskedMatriculaConsular}`
	}

	return undefined
}

function CustomerBeneficialOwnerRow({customerId, beneficialOwner: bo, refresh}: CustomerBeneficialOwnerRowProps) {
	const {address, dateOfBirth, email, fullName, percentage, phone} = bo.attributes
	const document = getBeneficialOwnerDocument(bo)

	const addressText = useMemo(() => {
		const street2 = address?.street2 ? `${address.street2}, ` : ""
		const city = address?.state
			? `${address?.city}, ${address.state} ${address.postalCode}, `
			: `${address?.city} ${address?.postalCode}, `

		return `${address?.street}, ${street2}${city}${address?.country}`
	}, [address?.street, address?.street2, address?.city, address?.state, address?.postalCode, address?.country])

	const isUnitComplianceUser = useIsUnitComplianceUser()
	const isBankOperationsUser = useIsBankOperationsUser()
	const isBankReadOnlyUser = useIsBankReadOnlyUser()
	const isHiddenIconVisible = !isUnitComplianceUser
	const isDefaultHidden = !isUnitComplianceUser && !isBankOperationsUser && !isBankReadOnlyUser
	const isUnitAdminUser = useIsUnitAdminUser()
	const [showEditModal, hideEditModal] = useModal(() => (
		<EditCustomerBeneficialOwnerModal
			customerId={customerId}
			beneficialOwner={bo}
			refresh={refresh}
			close={hideEditModal}
		/>
	))

	return (
		<DataTableRow>
			<DataTableCell>{bo.id}</DataTableCell>
			<DataTableCell>
				{fullName.first} {fullName.last}
			</DataTableCell>
			<DataTableCell>
				{document ? (
					isMasked(document) ? (
						document.replace(/[*]/g, "●")
					) : (
						<InformationHiding
							element={(document) => <>{document}</>}
							getValue={() => document ?? ""}
							placeholder={document ? identificationNumberPreview(document) : ""}
							defaultIsHidden={isDefaultHidden}
							isHiddenIconVisible={isHiddenIconVisible}
						/>
					)
				) : (
					hiddenValue
				)}
			</DataTableCell>
			<DataTableCell>
				{dateOfBirth ? (
					<InformationHiding
						element={(formattedDate) => <>{formattedDate}</>}
						getValue={() => moment(dateOfBirth).format("L")}
						placeholder={datePreview()}
						defaultIsHidden={isDefaultHidden}
						isHiddenIconVisible={isHiddenIconVisible}
					/>
				) : (
					hiddenValue
				)}
			</DataTableCell>
			<DataTableCell>{percentage ? `${percentage}%` : "N/A"}</DataTableCell>
			<DataTableCell>
				{phone ? (
					<InformationHiding
						element={(phoneNumber) => <>{phoneNumber}</>}
						getValue={() => `+${phone.countryCode} ${phone.number}`}
						placeholder={phonePreview(phone.number)}
						defaultIsHidden={isDefaultHidden}
						isHiddenIconVisible={isHiddenIconVisible}
					/>
				) : (
					hiddenValue
				)}
			</DataTableCell>
			<DataTableCell>
				{email ? (
					<InformationHiding
						element={(emailAddress) => <>{emailAddress}</>}
						getValue={() => email ?? ""}
						placeholder={emailPreview(email)}
						defaultIsHidden={isDefaultHidden}
						isHiddenIconVisible={isHiddenIconVisible}
					/>
				) : (
					hiddenValue
				)}
			</DataTableCell>
			<DataTableCell>{address ? addressText : hiddenValue}</DataTableCell>
			{isUnitAdminUser && (
				<DataTableActionButton enableActions={true}>
					<DataTableActionItem title={"Edit"} icon={"pencil-1--interface-essential"} onClick={() => showEditModal()} />
				</DataTableActionButton>
			)}
		</DataTableRow>
	)
}

export default function CustomerBeneficialOwners({
	customerId,
	beneficialOwners,
	refresh,
}: {
	customerId: string
	beneficialOwners: CustomerBeneficialOwner[]
	refresh: () => void
}) {
	const isUnitAdminUser = useIsUnitAdminUser()
	const rows = beneficialOwners.map((bo, index) => (
		<CustomerBeneficialOwnerRow customerId={customerId} beneficialOwner={bo} refresh={refresh} key={index} />
	))

	return (
		<DataTableCard className={"customer-beneficial-owners"}>
			<DataTableActionHeader title={"Beneficial Owners"} />
			<div className={"customer-beneficial-owners-table"}>
				<DataTable fullHeight={false} hoverable={false} clickable={false} isEmpty={beneficialOwners.length === 0}>
					<DataTableHead>
						<DataTableRow>
							<DataTableCell>Id</DataTableCell>
							<DataTableCell>Name</DataTableCell>
							<DataTableCell>Document</DataTableCell>
							<DataTableCell>Birth Date</DataTableCell>
							<DataTableCell>Percentage</DataTableCell>
							<DataTableCell>Phone</DataTableCell>
							<DataTableCell>Email</DataTableCell>
							<DataTableCell>Address</DataTableCell>
							{isUnitAdminUser && <DataTableCell>Actions</DataTableCell>}
						</DataTableRow>
					</DataTableHead>
					<DataTableBody>{rows}</DataTableBody>
				</DataTable>
			</div>
		</DataTableCard>
	)
}
